import type { Epic } from '@helpcenter/epics-rxjs';
import { Observable } from '@helpcenter/epics-rxjs';
import { isJsmPortalRovoIntegrationEnabled } from '@helpcenter/feature-flags';

import fetchModel from 'epics/model/fetcher';
import type { OrganisationResponse } from 'rest/organisations';
import type { RequestCreateResponse } from 'rest/request-create';
import { searchUser } from 'rest/user';
import type {
    FetchRequestCreateAction,
    FetchRequestCreateSuccess,
    FetchRequestCreateFailure,
} from 'state/actions/request-create';
import {
    FETCH_REQUEST_CREATE_MODEL,
    fetchRequestCreateSuccess,
    fetchRequestCreateFailure,
} from 'state/actions/request-create';
import type {
    CmdbObjectPickerState,
    DefaultLabel,
    FieldState,
    FieldValues,
    OrganisationPickerState,
    ReporterState,
    UserPickerState,
    MultiUserPickerState,
} from 'state/persisted/request-create';
import { toFieldStates } from 'utils/request-create';
import { parseFieldUrlParams } from 'utils/request-create/url-param-parser';

import type { User } from 'view/common/fields/user-picker/user-picker-dumb/user-picker-dumb';
import type { Option } from '@atlaskit/select';
import type { CmdbObjectResponse } from '@atlassian/cmdb-help-center-components/src/rest/cmdb-object-picker';
import { fetchCmdbObjectsWithSignal } from '@atlassian/cmdb-help-center-components/src/rest/cmdb-object-picker';
import type { CmdbObjectPickerOption } from '@atlassian/cmdb-help-center-components/src/ui/cmdb-object-picker/cmdb-object-picker-dumb';
import type { ErrorResponse } from '../types';

interface HydratedRequestCreateResponse {
    requestCreateResponse: RequestCreateResponse;
    hydratedUrlParamOverrides: FieldValues;
}

type FieldValueLoader<Value, State> = (
    fieldState: State,
    fieldValueOverrides: FieldValues,
    portalId: number,
    requestTypeId: number,
    fieldValueMap?: FieldValues
) => Promise<{
    fieldId: keyof FieldValues;
    value: Value;
}>;

interface FieldStateMap {
    [fieldId: string]: FieldState;
}

export const requestCreateEpic: Epic<
    FetchRequestCreateAction,
    FetchRequestCreateSuccess | FetchRequestCreateFailure
> = (action$) => {
    return action$.ofType(FETCH_REQUEST_CREATE_MODEL).mergeMap((action) => {
        const portalId = action.payload && action.payload.params && action.payload.params.portalId;
        const requestTypeId = action.payload && action.payload.params && action.payload.params.id;

        if (isJsmPortalRovoIntegrationEnabled()) {
            return fetchModel(action.payload)
                .filter((response) => !!response.reqCreate)
                .mergeMap((response) => {
                    return hydrateUrlParamOverrides(response.reqCreate!, portalId, requestTypeId).then(
                        ({ requestCreateResponse, hydratedUrlParamOverrides }) =>
                            fetchRequestCreateSuccess({
                                ...requestCreateResponse,
                                hydratedUrlParamOverrides,
                                portalId,
                            })
                    );
                })
                .catch((error: ErrorResponse) => {
                    const params = action.payload.params;
                    return Observable.of(fetchRequestCreateFailure(portalId, params.id, error));
                });
        }

        return fetchModel(action.payload)
            .filter((response) => !!response.reqCreate)
            .map((response) => {
                return fetchRequestCreateSuccess({ ...response.reqCreate!, portalId, hydratedUrlParamOverrides: {} });
            })
            .catch((error: ErrorResponse) => {
                const params = action.payload.params;
                return Observable.of(fetchRequestCreateFailure(portalId, params.id, error));
            });
    });
};

/**
 * Builds a set of URL param overrides from the query parameters, and attempts to hydrate any overrides
 * for dynamic fields.
 *
 * This should never break the request create flow. If a dynamic field cannot be hydrated successfully (failed
 * API call), this function will proceed as though a URL override was not provided for that field.
 */
const hydrateUrlParamOverrides = async (
    requestCreateResponse: RequestCreateResponse,
    portalId: number,
    requestTypeId: number
): Promise<HydratedRequestCreateResponse> => {
    const fieldStates = toFieldStates(requestCreateResponse.fields);
    const fieldStatesMap: FieldStateMap = Object.fromEntries(
        fieldStates.map((fieldState) => [fieldState.id, fieldState])
    );
    const fieldValueOverrides = parseFieldUrlParams(fieldStates);
    const asyncFieldStatesWithDependencies: FieldState[] = [];
    // This is a map used to store a representation of all current field values (including any hydrated ones)
    // so that it can be sent in the payload for CMDB queries. CMDB queries require this since AQL can filter
    // based on dependencies on other fields.
    const fieldValueMap = fieldStates.reduce((valueMap, field: FieldState) => {
        valueMap[field.id] = field.value?.toString();
        return valueMap;
    }, {} as FieldValues);

    // Mapping all dynamic pre-fill fields to promises so they can be hydrated in parallel.
    // Fields with dependencies need to be mapped last so we can populate the fielValueMap
    // as much as possible. The only known field with dependencies at the moment are CMDB fields.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const asyncFieldValuePromises: ReturnType<FieldValueLoader<any, FieldState>>[] = fieldStates
        .map((fieldState) => {
            switch (fieldState.type) {
                case 'cmdbobjectpicker':
                    // We don't resolve CMDB fields immediately because they may have dependencies that need to be resolved first
                    asyncFieldStatesWithDependencies.push(fieldState);
                    return Promise.resolve({ fieldId: fieldState.id, value: undefined });
                case 'userpicker':
                    return getUserPickerLoader(fieldState, fieldValueOverrides, portalId, requestTypeId);
                case 'reporter':
                    return getUserPickerLoader(fieldState, fieldValueOverrides, portalId, requestTypeId);
                case 'multiuserpicker':
                    return getMultiUserPickerLoader(fieldState, fieldValueOverrides, portalId, requestTypeId);
                case 'organisationpicker':
                    return getOrganisationPickerLoader(
                        fieldState,
                        fieldValueOverrides,
                        portalId,
                        requestTypeId,
                        undefined,
                        requestCreateResponse.userOrganisations
                    );
                // The following are static fields which have been converted into options. They need to be converted
                // to a different format for the fieldValueMap
                case 'labelpicker':
                    if (fieldValueOverrides[fieldState.id]) {
                        fieldValueMap[fieldState.id] = (fieldValueOverrides[fieldState.id] as DefaultLabel[])
                            .map((defaultLabel) => defaultLabel.value)
                            .toString();
                    }
                    return undefined;
                case 'select':
                    if (fieldValueOverrides[fieldState.id]) {
                        fieldValueMap[fieldState.id] = (fieldValueOverrides[fieldState.id] as Option<string>).value;
                    }
                    return undefined;
                case 'multiselect':
                    if (fieldValueOverrides[fieldState.id]) {
                        fieldValueMap[fieldState.id] = (fieldValueOverrides[fieldState.id] as Option<string>[])
                            .map((multiSelectOverride) => multiSelectOverride.value)
                            .toString();
                    }
                    return undefined;
                // All other static field types don't need to be hydrated or transformed, so we can store them
                // directly on fieldValueMap
                default:
                    // TODO: Work out the format for cascading select data. This is currently also broken in the existing
                    // CMDB field implementation so we're only being consistent with how cascading fields work in
                    // Portal view.
                    if (fieldValueOverrides[fieldState.id]) {
                        fieldValueMap[fieldState.id] = (fieldValueOverrides[fieldState.id] as object).toString();
                    }
                    return undefined;
            }
        })
        .filter((loader) => loader !== undefined);

    // When storing the hydrated results of dynamic fields, we need to keep asyncFieldValueOverrides in sync with
    // fieldValueMap. They need to store the same results, but in a different representation.
    const asyncFieldValueOverrides = await Promise.all(asyncFieldValuePromises).then((results) =>
        results.reduce((accumulator, result) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            accumulator[result.fieldId] = result.value;

            if (result.value === undefined) {
                return accumulator;
            }

            const fieldType = fieldStatesMap[result.fieldId].type;
            switch (fieldType) {
                case 'userpicker':
                case 'reporter': {
                    const userValue = result.value as User;
                    fieldValueMap[result.fieldId] = userValue.accountId;
                    break;
                }
                case 'multiuserpicker': {
                    const userValues = result.value as User[];
                    fieldValueMap[result.fieldId] = userValues.map((user) => user.accountId).toString();
                    break;
                }
                case 'organisationpicker': {
                    const organisationValue = result.value as OrganisationResponse;
                    fieldValueMap[result.fieldId] = organisationValue.id.toString();
                    break;
                }
                default:
                    break;
            }

            return accumulator;
        }, {} as FieldValues)
    );

    // A for loop is needed because CMDB fields can depend on other CMDB fields. Hence we need to hydrate them one
    // at a time and make subsequent calls with an updated fieldValueMap.
    for (const fieldState of asyncFieldStatesWithDependencies) {
        const fieldId = fieldState.id;
        switch (fieldState.type) {
            case 'cmdbobjectpicker': {
                const cmdbPickerValue =
                    // eslint-disable-next-line no-await-in-loop
                    (await getCmdbObjectLoader(fieldState, fieldValueOverrides, portalId, requestTypeId, fieldValueMap))
                        .value;

                asyncFieldValueOverrides[fieldId] = cmdbPickerValue;
                if (cmdbPickerValue === undefined) {
                    break;
                }
                fieldValueMap[fieldId] = cmdbPickerValue.map((value) => value.value).toString();
                break;
            }
            default:
                break;
        }
    }

    const hydratedUrlParamOverrides: FieldValues = Object.fromEntries(
        Object.entries({ ...fieldValueOverrides, ...asyncFieldValueOverrides }).filter(
            ([, fieldValue]) => fieldValue !== undefined
        )
    );

    return { requestCreateResponse, hydratedUrlParamOverrides };
};

const getMultiUserPickerLoader: FieldValueLoader<User[] | undefined, MultiUserPickerState> = (
    fieldState: MultiUserPickerState,
    fieldValueOverrides: FieldValues,
    portalId: number
) => {
    const fieldId = fieldState.id;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const multiUserPickerQuery = fieldValueOverrides[fieldState.id];

    if (typeof multiUserPickerQuery !== 'string') {
        return Promise.resolve({
            fieldId,
            value: undefined,
        });
    }

    // Split comma-separated user queries and search for each
    const userQueries = multiUserPickerQuery
        .split(',')
        .map((query) => query.trim())
        .filter((query) => query.length > 0);

    if (userQueries.length === 0) {
        return Promise.resolve({
            fieldId,
            value: undefined,
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const userSearchPromises: Promise<any>[] = userQueries.map((query) =>
        searchUser({
            portalId,
            query,
            fieldName: fieldId,
            fieldConfigId: fieldState.fieldConfigId,
        })
            .toPromise()
            .then((userSearchResponse) => {
                if (userSearchResponse.length === 0) {
                    return undefined;
                }
                // Return the first matching user for this query
                const userResult = userSearchResponse[0];
                return {
                    accountId: userResult.accountId,
                    email: userResult.emailAddress,
                    iconUrl: userResult.avatar,
                    label: userResult.displayName,
                    value: userResult.accountId,
                };
            })
            .catch(() => undefined)
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Promise.all(userSearchPromises).then((results: any[]) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const users = results.filter((user: any) => user !== undefined) as User[];
        return {
            fieldId: fieldState.id,
            value: users.length > 0 ? users : undefined,
        };
    });
};
const getUserPickerLoader: FieldValueLoader<User | undefined, UserPickerState | ReporterState> = (
    fieldState: UserPickerState | ReporterState,
    fieldValueOverrides: FieldValues,
    portalId: number
) => {
    const fieldId = fieldState.id;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const userPickerQuery = fieldValueOverrides[fieldState.id];

    if (typeof userPickerQuery !== 'string') {
        return Promise.resolve({
            fieldId,
            value: undefined,
        });
    }

    return searchUser({
        portalId,
        query: userPickerQuery,
        fieldName: fieldId,
        fieldConfigId: fieldState.fieldConfigId,
    })
        .toPromise()
        .then((userSearchResponse) => {
            if (userSearchResponse.length === 0) {
                return {
                    fieldId,
                    value: undefined,
                };
            }

            const firstUserResult = userSearchResponse[0];
            return {
                fieldId: fieldState.id,
                value: {
                    accountId: firstUserResult.accountId,
                    email: firstUserResult.emailAddress,
                    iconUrl: firstUserResult.avatar,
                    label: firstUserResult.displayName,
                    value: firstUserResult.accountId,
                },
            };
        })
        .catch(() => ({
            fieldId,
            value: undefined,
        }));
};

const getOrganisationPickerLoader = (
    fieldState: OrganisationPickerState,
    fieldValueOverrides: FieldValues,
    portalId: number,
    requestTypeId: number,
    fieldValueMap?: FieldValues,
    userOrganisations?: { id: number; name: string }[]
) => {
    const fieldId = fieldState.id;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const organisationPickerQuery = fieldValueOverrides[fieldState.id];

    if (typeof organisationPickerQuery !== 'string' || !userOrganisations) {
        return Promise.resolve({
            fieldId,
            value: undefined,
        });
    }

    // Try to match the URL parameter against user's organisations
    // First, try to match by ID (if the query is numeric)
    const queryAsId = parseInt(organisationPickerQuery, 10);
    if (!isNaN(queryAsId)) {
        const matchedOrgById = userOrganisations.find((org) => org.id === queryAsId);
        if (matchedOrgById) {
            return Promise.resolve({
                fieldId,
                value: {
                    id: matchedOrgById.id,
                    name: matchedOrgById.name,
                    domain: '',
                },
            });
        }
    }

    // Try to match by name (case-insensitive)
    const matchedOrgByName = userOrganisations.find(
        (org) => org.name.toLowerCase() === organisationPickerQuery.toLowerCase()
    );
    if (matchedOrgByName) {
        return Promise.resolve({
            fieldId,
            value: {
                id: matchedOrgByName.id,
                name: matchedOrgByName.name,
                domain: '',
            },
        });
    }

    // No matching organisation found
    return Promise.resolve({
        fieldId,
        value: undefined,
    });
};

const getCmdbObjectLoader: FieldValueLoader<CmdbObjectPickerOption[] | undefined, CmdbObjectPickerState> = (
    fieldState: CmdbObjectPickerState,
    fieldValueOverrides: FieldValues,
    portalId: number,
    requestTypeId: number,
    fieldValueMap: FieldValues
) => {
    const fieldId = fieldState.id;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const cmdbPickerQuery = fieldValueOverrides[fieldState.id];
    const abortController = new AbortController();
    const { signal } = abortController;

    if (typeof cmdbPickerQuery === 'string') {
        return getSingleCmdbObjectLoader(signal, cmdbPickerQuery, fieldId, fieldValueMap, portalId, requestTypeId).then(
            (cmdbResult) =>
                cmdbResult.value === undefined
                    ? { fieldId: cmdbResult.fieldId, value: undefined }
                    : { fieldId: cmdbResult.fieldId, value: [cmdbResult.value] }
        );
    }

    if (Array.isArray(cmdbPickerQuery)) {
        const cmdbObjectPickerQueries = cmdbPickerQuery.filter((query) => typeof query === 'string');
        const cmdbObjectPickerPromises = cmdbObjectPickerQueries.map((query) =>
            getSingleCmdbObjectLoader(signal, query, fieldId, fieldValueMap, portalId, requestTypeId)
        );
        return Promise.all(cmdbObjectPickerPromises).then((results) => {
            const options = results.map((loaderResult) => loaderResult.value).filter((option) => option !== undefined);
            return {
                fieldId,
                value: options.length === 0 ? undefined : options,
            };
        });
    }

    return Promise.resolve({
        fieldId,
        value: undefined,
    });
};

const getSingleCmdbObjectLoader = (
    abortSignal: AbortSignal,
    cmdbPickerQuery: string,
    fieldId: string,
    fieldValueMap: FieldValues,
    portalId: number,
    requestTypeId: number
): ReturnType<FieldValueLoader<CmdbObjectPickerOption | undefined, FieldState>> => {
    return fetchCmdbObjectsWithSignal(
        portalId,
        requestTypeId,
        fieldId,
        cmdbPickerQuery,
        Object.entries(fieldValueMap).filter(([_, fieldValue]) => !!fieldValue),
        abortSignal,
        0
    )
        .then((response: CmdbObjectResponse) => {
            const responseObjects = response.cmdbObjects;
            if (responseObjects.length === 0) {
                return {
                    fieldId,
                    value: undefined,
                };
            }

            const firstResult = responseObjects[0];
            return {
                fieldId,
                value: {
                    cmdbObject: firstResult,
                    label: firstResult.label,
                    value: firstResult.id,
                },
            };
        })
        .catch(() => ({
            fieldId,
            value: undefined,
        }));
};
